import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { forwardRef, useRef, useState, useEffect } from 'react';


const FormattedInput = forwardRef(
  (
    {
      type = 'text',
      variant = 'underline',
      placeholder,
      error = false,
      classes,
      value,
      readOnly = false,
      onChange,
      onInvalid,
      required,
      setValue,
      setValueName,
      formatFunction, // 포매팅 함수
      fieldName, // setValue에서 사용할 필드명
      ...props
    },
    ref,
  ) => {
    const defaultRef = useRef();
    const inputRef = ref ?? defaultRef;
    const [isFocused, setIsFocused] = useState(false);

    // 내부적으로 보이는 값(포맷 적용된 값)을 관리
    const [displayValue, setDisplayValue] = useState(() => {
      const initial = value ?? '';
      const digits = String(initial).replace(/[^0-9]/g, '').slice(0, 10);
      return formatFunction ? formatFunction(digits) : digits;
    });

    useEffect(() => {
      const v = value ?? '';
      const digits = String(v).replace(/[^0-9]/g, '').slice(0, 10);
      setDisplayValue(formatFunction ? formatFunction(digits) : digits);
    }, [value, formatFunction]);

    // register로 전달된 핸들러/ref 추출
    const registerOnChange = props.onChange;
    const registerOnBlur = props.onBlur;
    const registerRef = props.ref;
    const registerName = props.name;

    const handleRef = (el) => {
      inputRef.current = el;
      if (typeof registerRef === 'function') registerRef(el);
      else if (registerRef && typeof registerRef === 'object') registerRef.current = el;
    };

    const handleChange = (e) => {
      const raw = e.target.value;
      const digits = String(raw).replace(/[^0-9]/g, '').slice(0, 10);
      const formatted = formatFunction ? formatFunction(digits) : digits;

      // 보이는 값은 포맷된 값
      setDisplayValue(formatted);

      // 폼과 부모 콜백에는 하이픈 없는 숫자 전달
      if (setValue && fieldName) {
        setValue(fieldName, digits);
      }

      if (typeof registerOnChange === 'function') {
        registerOnChange({ target: { value: digits, name: registerName } });
      }
      if (typeof onChange === 'function') {
        onChange({ target: { value: digits, name: registerName } });
      }
    };

    return (
      <div
        className={classNames(
          error && 'error',
          classes,
          'input'
        )}
      >
        <input
          name={registerName}
          ref={handleRef}
          value={displayValue}
          type={type}
          onChange={handleChange}
          placeholder={placeholder}
          readOnly={readOnly}
          className={classNames(
            classes
          )}
          autoComplete='new-password'

          onInput={e => {
            if (['number', 'tel'].includes(type)) {
              const target = e.currentTarget;
              const hasInvalidKey = /[^0-9]/g.test(target.value);

              if (hasInvalidKey) {
                const { selectionStart } = target;
                target.value = target.value.replace(/[^0-9]*/g, '');
                if (typeof selectionStart === 'undefined' || selectionStart === null) {
                  target.setSelectionRange(selectionStart - 1, selectionStart - 1);
                }
                e.preventDefault();
              }
            }
            props.onInput?.(e);
          }}
          onFocus={e => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={e => {
            setIsFocused(false);
            if (typeof registerOnBlur === 'function') registerOnBlur(e);
            props.onBlur?.(e);
          }}
        />
        {error.message && <span className={classNames('error')}>{error.message}</span>}
      </div>
    );
  },
);
export default FormattedInput;

FormattedInput.propTypes = {
  type: PropTypes.oneOf(['text', 'number', 'email', 'password', 'search', 'tel']),
  variant: PropTypes.oneOf(['underline', 'white', 'grey']),
  formatFunction: PropTypes.func,
  fieldName: PropTypes.string,
  classes: PropTypes.string,
};
